import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gift, Heart, Cake, TreePine, HeartHandshake, UtensilsCrossed, Search } from "lucide-react";
import { API } from "@/App";
import { useLanguage } from "@/LanguageContext";
import { toast } from "sonner";
import { GiftCardPreview } from "@/components/GiftCardPreview";
import { Link } from "react-router-dom";

const occasions = [
  { id: "none", label: "Smak av India", desc: "Masala Indisk", icon: UtensilsCrossed },
  { id: "birthday", label: "Bursdag", desc: "Gratulerer med dagen!", icon: Cake },
  { id: "mothersday", label: "Morsdag", desc: "Til verdens beste mamma", icon: HeartHandshake },
  { id: "christmas", label: "Jul", desc: "God jul!", icon: TreePine },
];

const GiftCardPage = () => {
  const [formData, setFormData] = useState({
    amount: 500,
    purchaser_email: "",
    recipient_email: "",
    sender_name: "",
    recipient_name: "",
    occasion: "none",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const { t } = useLanguage();

  const presetAmounts = [250, 500, 750, 1000, 1500];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.sender_name.trim()) {
      toast.error(t("gc_from") + " *");
      return;
    }
    if (!formData.recipient_name.trim()) {
      toast.error(t("gc_to") + " *");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/gift-cards/purchase`, formData);
      if (response.data.payment_url) {
        window.location.href = response.data.payment_url;
      }
    } catch (error) {
      console.error("Gift card purchase error:", error);
      toast.error("Kunne ikke behandle gavekort. Vennligst prøv igjen.");
      setSubmitting(false);
    }
  };

  const selectedOccasion = occasions.find((o) => o.id === formData.occasion);

  return (
    <div className="min-h-screen" style={{ background: "#f4e7d8" }}>
      <Navbar />

      {/* Header */}
      <div
        className="py-16 mandala-pattern"
        style={{ background: "linear-gradient(135deg, #721111 0%, #2d1810 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="indian-divider mb-4">
            <span className="text-sm uppercase tracking-widest" style={{ color: "#d4af37" }}>{t("gc_tag")}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-serif-display font-semibold italic mb-4"
            style={{ color: "#d4af37" }}
            data-testid="giftcard-page-title"
          >
            {t("gc_title")}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} style={{ color: "#f5e6d3" }}>
            {t("gc_subtitle")}
          </motion.p>
        </div>
      </div>

      <div className="h-2" style={{ background: "linear-gradient(90deg, #721111, #d4af37, #721111)" }}></div>

      {/* Balance check link */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-0">
        <Link
          to="/gift-cards/saldo"
          className="inline-flex items-center gap-2 text-sm font-semibold hover:underline transition-colors"
          style={{ color: "#d4a847" }}
          data-testid="check-balance-link"
        >
          <Search className="h-4 w-4" /> Har du allerede et gavekort? Sjekk saldo her
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8 min-w-0">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-8 flex-1 min-w-0"
            style={{ border: "2px solid #d4af37" }}
            data-testid="giftcard-form-container"
          >
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Step 1: Occasion */}
            <div>
              <h2 className="text-xl font-serif-display font-semibold mb-4" style={{ color: "#2d1810" }}>
                1. Velg Anledning
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="occasion-selector">
                {occasions.map((occ) => {
                  const Icon = occ.icon;
                  const isActive = formData.occasion === occ.id;
                  return (
                    <button
                      key={occ.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, occasion: occ.id })}
                      className={`p-4 rounded-xl border-2 text-center transition-all hover:scale-105 ${isActive ? "shadow-lg" : ""}`}
                      style={{
                        borderColor: isActive ? "#d4af37" : "#e5e7eb",
                        background: isActive ? "#fdf8f0" : "white",
                      }}
                      data-testid={`occasion-${occ.id}`}
                    >
                      <Icon className="h-6 w-6 mx-auto mb-2" style={{ color: isActive ? "#721111" : "#9ca3af" }} />
                      <div className="font-semibold text-sm" style={{ color: "#2d1810" }}>{occ.label}</div>
                      <div className="text-xs mt-1" style={{ color: "#8b4513" }}>{occ.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Amount */}
            <div>
              <h2 className="text-xl font-serif-display font-semibold mb-4" style={{ color: "#2d1810" }}>
                {t("gc_step2")}
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-3">
                {presetAmounts.map((amount) => (
                  <Button
                    key={amount}
                    type="button"
                    className={formData.amount === amount ? "btn-maroon" : "bg-white hover:bg-stone-50"}
                    style={formData.amount !== amount ? { border: "1px solid #b27f47", color: "#2d1810" } : {}}
                    onClick={() => setFormData({ ...formData, amount })}
                    data-testid={`preset-amount-${amount}`}
                  >
                    kr {amount},-
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm" style={{ color: "#4a3728" }}>{t("gc_custom")}</span>
                <Input
                  type="number"
                  min="100"
                  step="50"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  className="indian-input max-w-[160px]"
                  data-testid="custom-amount-input"
                />
                <span className="text-sm" style={{ color: "#4a3728" }}>NOK</span>
              </div>
            </div>

            {/* Step 3: Names */}
            <div>
              <h2 className="text-xl font-serif-display font-semibold mb-4" style={{ color: "#2d1810" }}>
                {t("gc_step3")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label style={{ color: "#2d1810" }}>{t("gc_from")} *</Label>
                  <Input
                    value={formData.sender_name}
                    onChange={(e) => setFormData({ ...formData, sender_name: e.target.value })}
                    placeholder={t("gc_from_placeholder")}
                    required
                    className="indian-input mt-1"
                    data-testid="sender-name-input"
                  />
                </div>
                <div>
                  <Label style={{ color: "#2d1810" }}>{t("gc_to")} *</Label>
                  <Input
                    value={formData.recipient_name}
                    onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                    placeholder={t("gc_to_placeholder")}
                    required
                    className="indian-input mt-1"
                    data-testid="recipient-name-input"
                  />
                </div>
              </div>
            </div>

            {/* Step 4: Emails */}
            <div>
              <h2 className="text-xl font-serif-display font-semibold mb-4" style={{ color: "#2d1810" }}>
                {t("gc_step4")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="purchaser_email" style={{ color: "#2d1810" }}>{t("gc_your_email")} *</Label>
                  <Input
                    id="purchaser_email"
                    type="email"
                    value={formData.purchaser_email}
                    onChange={(e) => setFormData({ ...formData, purchaser_email: e.target.value })}
                    required
                    placeholder="din.epost@eksempel.no"
                    className="indian-input mt-1"
                    data-testid="purchaser-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="recipient_email" style={{ color: "#2d1810" }}>
                    {t("gc_recipient_email")}
                  </Label>
                  <Input
                    id="recipient_email"
                    type="email"
                    value={formData.recipient_email}
                    onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                    placeholder="mottaker@eksempel.no"
                    className="indian-input mt-1"
                    data-testid="recipient-email-input"
                  />
                  <p className="text-xs mt-1" style={{ color: "#4a3728" }}>{t("gc_recipient_email_hint")}</p>
                </div>
              </div>
            </div>

            {/* Step 5: Message */}
            <div>
              <h2 className="text-xl font-serif-display font-semibold mb-4" style={{ color: "#2d1810" }}>
                {t("gc_step5")}
              </h2>
              <Input
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder={t("gc_message_placeholder")}
                className="indian-input"
                data-testid="message-input"
              />
            </div>

            {/* Summary */}
            <div className="p-6 rounded-xl" style={{ background: "#f4e7d8", border: "2px solid #d4af37" }}>
              <h3 className="font-serif-display font-semibold mb-4" style={{ color: "#2d1810" }}>{t("gc_summary")}</h3>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <div>
                  <span style={{ color: "#8b4513" }}>{t("gc_occasion_label")}</span>
                  <span className="ml-2 font-semibold" style={{ color: "#2d1810" }}>{selectedOccasion ? t(selectedOccasion.labelKey) : ""}</span>
                </div>
                <div>
                  <span style={{ color: "#8b4513" }}>{t("gc_from_label")}</span>
                  <span className="ml-2 font-semibold" style={{ color: "#2d1810" }}>{formData.sender_name || "—"}</span>
                </div>
                <div>
                  <span style={{ color: "#8b4513" }}>{t("gc_to_label")}</span>
                  <span className="ml-2 font-semibold" style={{ color: "#2d1810" }}>{formData.recipient_name || "—"}</span>
                </div>
                <div>
                  <span style={{ color: "#8b4513" }}>{t("gc_valid_label")}</span>
                  <span className="ml-2 font-semibold" style={{ color: "#2d1810" }}>{t("gc_valid_1year")}</span>
                </div>
              </div>
              <div className="flex justify-between items-center pt-4" style={{ borderTop: "2px solid #d4af37" }}>
                <span className="text-lg font-semibold" style={{ color: "#2d1810" }}>{t("gc_total")}</span>
                <span className="text-3xl font-bold" style={{ color: "#721111" }} data-testid="total-amount">
                  kr {formData.amount},-
                </span>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-full font-bold py-6 btn-maroon transition-transform hover:scale-105 active:scale-95 shadow-lg"
              data-testid="purchase-giftcard-button"
            >
              <Gift className="h-5 w-5 mr-2" />
              {submitting ? t("gc_buying") : t("gc_buy_btn")}
            </Button>
          </form>
        </motion.div>

          {/* Live Preview — sticky sidebar on desktop, below form on mobile */}
          <div className="w-full lg:w-[380px] lg:flex-shrink-0">
            <div className="lg:sticky lg:top-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <GiftCardPreview formData={formData} />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-6 p-5 bg-white rounded-xl"
                style={{ border: "2px solid #b27f47" }}
              >
                <h3 className="font-serif-display font-semibold mb-3" style={{ color: "#2d1810" }}>
                  Gavekort Informasjon
                </h3>
                <ul className="space-y-2 text-sm" style={{ color: "#4a3728" }}>
                  <li>• Gyldig i 1 år fra kjøpsdato</li>
                  <li>• Ikke refunderbart og kan ikke byttes mot kontanter</li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GiftCardPage;
